import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Package, Search, Loader2, CheckCircle2, Clock, Truck, 
  Calendar, AlertCircle, ArrowRight, Gem
} from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Logo URL
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fa4ec7f5-8e6d-45d3-846f-706607abd5cf/artifacts/fge6e27b_logo-01.jpg';

const STATUS_STEPS = [
  { key: 'Order Received', label: 'Order Received', icon: Package, color: 'blue' },
  { key: 'In Production', label: 'In Production', icon: Gem, color: 'amber' },
  { key: 'Certification', label: 'Certification', icon: CheckCircle2, color: 'purple' },
  { key: 'Out for Delivery', label: 'Out for Delivery', icon: Truck, color: 'green' },
];

const CustomerTrack = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      setError('Please enter your order number');
      return;
    }

    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const response = await axios.get(`${API_URL}/track/${orderNumber.trim()}`);
      setOrder(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Order not found. Please check your order number and try again.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (stepKey) => {
    if (!order) return 'pending';
    const currentIndex = STATUS_STEPS.findIndex(s => s.key === order.status);
    const stepIndex = STATUS_STEPS.findIndex(s => s.key === stepKey);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-neutral-950 dark:to-neutral-900">
      {/* Header */}
      <header className="border-b border-slate-200/60 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-2 md:gap-3">
            <img 
              src={LOGO_URL} 
              alt="Siddhi Jewels Logo" 
              className="h-10 md:h-14 w-auto object-contain"
            />
            <p className="text-[10px] md:text-xs text-slate-500 border-l border-slate-200 dark:border-neutral-700 pl-2 md:pl-3">Order Tracking</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-12">
        {/* Search Card */}
        <Card className="mb-6 md:mb-8 shadow-lg border-slate-200/60">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-xl md:text-2xl">Track Your Order</CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Enter your order number (CO number) to check status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="orderNumber" className="sr-only">Order Number</Label>
                <Input
                  id="orderNumber"
                  type="text"
                  placeholder="e.g., SJ-202602-0001"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  className="h-11 md:h-12 text-base md:text-lg"
                  data-testid="track-order-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-11 md:h-12 px-6 md:px-8 bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20"
                disabled={loading}
                data-testid="track-order-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Track
                  </>
                )}
              </Button>
            </form>

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Order Details */}
        {order && (
          <div className="space-y-4 md:space-y-6 animate-slide-in" data-testid="order-result">
            {/* Order Summary Card */}
            <Card className="shadow-lg border-slate-200/60 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-amber-100 text-xs md:text-sm">Order Number</p>
                    <h2 className="text-xl md:text-2xl font-bold">{order.order_number}</h2>
                    <p className="text-amber-100 text-sm mt-1">Customer: {order.customer_name}</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <Badge className="bg-white/20 text-white text-sm md:text-lg px-3 md:px-4 py-1">
                      {order.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <CardContent className="p-4 md:p-6">
                {/* Progress Steps - Horizontal on desktop, simpler on mobile */}
                <div className="mb-6 md:mb-8">
                  {/* Desktop Progress Steps */}
                  <div className="hidden sm:flex items-center justify-between mb-4">
                    {STATUS_STEPS.map((step, index) => {
                      const status = getStepStatus(step.key);
                      const Icon = step.icon;
                      
                      return (
                        <React.Fragment key={step.key}>
                          <div className="flex flex-col items-center">
                            <div className={cn(
                              "h-10 w-10 md:h-12 md:w-12 rounded-full flex items-center justify-center transition-all",
                              status === 'completed' && "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20",
                              status === 'current' && "bg-amber-100 text-amber-600 dark:bg-amber-500/20 ring-4 ring-amber-100",
                              status === 'pending' && "bg-slate-100 text-slate-400 dark:bg-neutral-800"
                            )}>
                              {status === 'completed' ? (
                                <CheckCircle2 className="h-5 w-5 md:h-6 md:w-6" />
                              ) : (
                                <Icon className="h-5 w-5 md:h-6 md:w-6" />
                              )}
                            </div>
                            <p className={cn(
                              "text-[10px] md:text-xs mt-2 text-center max-w-[70px] md:max-w-[80px]",
                              status === 'current' ? "font-semibold text-amber-600" : "text-slate-500"
                            )}>
                              {step.label}
                            </p>
                          </div>
                          {index < STATUS_STEPS.length - 1 && (
                            <div className={cn(
                              "flex-1 h-1 mx-1 md:mx-2 rounded",
                              getStepStatus(STATUS_STEPS[index + 1].key) !== 'pending' 
                                ? "bg-emerald-400" 
                                : "bg-slate-200 dark:bg-neutral-700"
                            )} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  
                  {/* Mobile Progress Steps - Vertical */}
                  <div className="sm:hidden space-y-3">
                    {STATUS_STEPS.map((step, index) => {
                      const status = getStepStatus(step.key);
                      const Icon = step.icon;
                      
                      return (
                        <div key={step.key} className="flex items-center gap-3">
                          <div className={cn(
                            "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
                            status === 'completed' && "bg-emerald-100 text-emerald-600",
                            status === 'current' && "bg-amber-100 text-amber-600 ring-2 ring-amber-300",
                            status === 'pending' && "bg-slate-100 text-slate-400"
                          )}>
                            {status === 'completed' ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              <Icon className="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={cn(
                              "text-sm",
                              status === 'current' ? "font-semibold text-amber-600" : "text-slate-500"
                            )}>
                              {step.label}
                            </p>
                          </div>
                          {status === 'completed' && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                  <div className="p-3 md:p-4 rounded-xl bg-slate-50 dark:bg-neutral-900 text-center">
                    <p className="text-[10px] md:text-sm text-slate-500 mb-1">Total Pieces</p>
                    <p className="text-lg md:text-2xl font-bold text-slate-900 dark:text-white">
                      {order.pieces_ordered}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-center">
                    <p className="text-[10px] md:text-sm text-emerald-600 mb-1">Ready</p>
                    <p className="text-lg md:text-2xl font-bold text-emerald-600">
                      {order.pieces_ready}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-center">
                    <p className="text-[10px] md:text-sm text-amber-600 mb-1">In Progress</p>
                    <p className="text-lg md:text-2xl font-bold text-amber-600">
                      {order.pieces_in_progress}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-center">
                    <p className="text-[10px] md:text-sm text-blue-600 mb-1">Progress</p>
                    <p className="text-lg md:text-2xl font-bold text-blue-600">
                      {order.progress_percentage}%
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4 md:mt-6">
                  <div className="flex justify-between text-xs md:text-sm mb-2">
                    <span className="text-slate-500">Overall Completion</span>
                    <span className="font-semibold">{order.progress_percentage}%</span>
                  </div>
                  <Progress value={order.progress_percentage} className="h-2 md:h-3" />
                </div>

                {/* Delivery Info */}
                <div className="mt-4 md:mt-6 p-3 md:p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 border border-blue-100 dark:border-blue-500/20">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm text-blue-600 dark:text-blue-400">Expected Delivery</p>
                      <p className="text-base md:text-xl font-bold text-slate-900 dark:text-white truncate">
                        {formatDate(order.expected_delivery)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] md:text-sm text-slate-500">Days Left</p>
                      <p className={cn(
                        "text-xl md:text-2xl font-bold",
                        order.days_remaining <= 3 ? "text-red-500" : "text-emerald-500"
                      )}>
                        {order.days_remaining}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Help Card */}
            <Card className="bg-slate-50 dark:bg-neutral-900 border-slate-200/60">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm md:text-base text-slate-900 dark:text-white mb-1">
                      Need Help?
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500">
                      Contact your sales representative with your order number for any questions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Initial State - No order searched yet */}
        {!order && !error && !loading && (
          <div className="text-center py-8 md:py-12">
            <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center mx-auto mb-4 md:mb-6">
              <Package className="h-8 w-8 md:h-10 md:w-10 text-amber-600" />
            </div>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Track Your Jewellery Order
            </h3>
            <p className="text-sm md:text-base text-slate-500 max-w-md mx-auto px-4">
              Enter your order number above to see the current status and expected delivery date.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200/60 dark:border-neutral-800 py-4 md:py-6 mt-8 md:mt-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xs md:text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Siddhi Jewels. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CustomerTrack;
